import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { getColumnClassName } from './columnWidthStyles';
import { NewTableColumn, SortDirection } from './types';
import { tw } from 'utils/tailwindUtils';

interface Props<T> {
  columns: NewTableColumn<T>[];
  tableId: string;
  hasRowActions: boolean;
  sortBy?: string;
  sortDirection?: SortDirection;
  onSortChange?: (columnKey: string, direction: SortDirection) => void;
}

const alignClass = (align?: 'start' | 'end' | 'center') =>
  align === 'end'
    ? 'justify-end text-right'
    : align === 'center'
    ? 'justify-center text-center'
    : '';

function NewTableHeader<T>({
  columns,
  tableId,
  hasRowActions,
  sortBy,
  sortDirection,
  onSortChange
}: Props<T>) {
  const handleSortClick = (column: NewTableColumn<T>) => {
    if (!onSortChange) {
      return;
    }
    if (sortBy !== column.key) {
      onSortChange(column.key, 'asc');
      return;
    }
    onSortChange(column.key, sortDirection === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="flex w-max min-w-full items-center gap-4 px-6 py-2 text-xs font-medium text-inkMuted dark:text-inkMuted-dark">
      <div className="flex flex-1 min-w-0 items-center justify-between gap-4">
        {columns.map((column) => {
          const isStickyRight = column.sticky === 'right';
          const stickyClass = isStickyRight
            ? 'sticky right-0 z-10 px-2 bg-secondary-50 dark:bg-dark-600'
            : '';

          const content =
            column.sortable && onSortChange ? (
              <button
                key={column.key}
                type="button"
                onClick={() => handleSortClick(column)}
                className={
                  'flex items-center gap-1 transition hover:text-secondary-600 dark:hover:text-secondary-300 '
                }
              >
                {column.label}
                {sortBy === column.key ? (
                  sortDirection === 'asc' ? (
                    <ChevronUp size={13} />
                  ) : (
                    <ChevronDown size={13} />
                  )
                ) : (
                  <ChevronsUpDown size={13} className="opacity-40" />
                )}
              </button>
            ) : (
              <span key={column.key} className={'flex items-center'}>
                {column.label}
              </span>
            );
          return (
            <div
              key={column.key}
              className={tw(
                'flex shrink-0 items-center',
                getColumnClassName(tableId, column.key),
                alignClass(column.align),
                stickyClass
              )}
            >
              {content}
            </div>
          );
        })}
      </div>
      {hasRowActions && (
        <span
          className={tw('shrink-0', getColumnClassName(tableId, 'actions'))}
        />
      )}
    </div>
  );
}

export default NewTableHeader;
